import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Rating,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_URL } from '../config';
import { format } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import { localizeScenarioTitle } from '../i18n/scenarios';

// Helper to render evaluation status
const renderEvaluationStatus = (evaluation, tr) => {
  if (evaluation === 'correct') {
    return <Chip label={tr('正確', 'Correct')} color="success" size="small" icon={<CheckCircleIcon />} variant="outlined" sx={{ mr: 1 }} />;
  }
  if (evaluation === 'incorrect') {
    return <Chip label={tr('不正確', 'Incorrect')} color="error" size="small" icon={<CancelIcon />} variant="outlined" sx={{ mr: 1 }} />;
  }
  return null;
};

// Helper to render boolean status with icons
const renderBooleanStatus = (value, trueLabel = "是", falseLabel = "否", trueColor = "error", falseColor = "success") => {
  return value ? (
    <Chip label={trueLabel} color={trueColor} size="small" icon={<CancelIcon />} variant="outlined" sx={{ mr: 1 }} />
  ) : (
    <Chip label={falseLabel} color={falseColor} size="small" icon={<CheckCircleIcon />} variant="outlined" sx={{ mr: 1 }} />
  );
};

function EvaluationDetail() {
  const { tr, isEnglish } = useLanguage();
  const { evaluationId } = useParams();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvaluation = async () => {
      if (!evaluationId) {
        setError("No evaluation ID provided.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/api/evaluations/${evaluationId}`);
        setEvaluation(response.data);
      } catch (err) {
        console.error(`Error fetching evaluation ${evaluationId}:`, err);
        if (err.response && err.response.status === 404) {
          setError(`Evaluation with ID ${evaluationId} not found.`);
        } else {
          setError(err.response?.data?.error || err.message || 'Failed to fetch evaluation details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluation();
  }, [evaluationId]);

  // Function to render detailed sections
  const renderDetailSection = (title, data, renderItem) => (
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 2 }}>
        {title}
      </Typography>
      {Array.isArray(data) && data.length > 0 ? (
        <List dense disablePadding>
          {data.map((item, index) => renderItem(item, index))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">{tr('無此項目的評估資料。', 'No evaluation data for this item.')}</Typography>
      )}
    </Paper>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 8 }}>
      <Button
        component={RouterLink}
        to="/evaluations" // Link back to the list
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        {tr('返回列表', 'Back to list')}
      </Button>
      <Typography variant="h4" gutterBottom component="h1">
        {tr('評估詳情', 'Evaluation Details')} (ID: {evaluationId})
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      )}

      {!loading && !error && evaluation && (
        <Box>
            <Paper sx={{ p: 3, mb: 3, bgcolor: '#f7f9fc' }}>
                 <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2"><strong>{tr('對話 ID:', 'Conversation ID:')}</strong> {evaluation.conversation_id || 'N/A'}</Typography>
                    </Grid>
                     <Grid item xs={12} sm={6}>
                        <Typography variant="body2"><strong>{tr('場景:', 'Scenario:')}</strong> {localizeScenarioTitle(evaluation.scenario_title, evaluation.scenario_id, isEnglish) || tr('未知', 'Unknown')}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2"><strong>{tr('提交者 ID:', 'Submitted By:')}</strong> {evaluation.member_id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2"><strong>{tr('提交時間:', 'Submitted At:')}</strong> {evaluation.created_at ? format(new Date(evaluation.created_at), 'yyyy-MM-dd HH:mm:ss') : 'N/A'}</Typography>
                    </Grid>
                 </Grid>
            </Paper>

          {renderDetailSection(tr('A1 - 節點正確性', 'A1 - Node Accuracy'), evaluation.a1_correctness,
            (item, index) => (
              <ListItem key={index} divider sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                <ListItemText
                  primary={`${tr('節點', 'Node')}: ${item.nodeTitle}`}
                  secondary={`${tr('專員訊息', 'Advisor message')}: "${item.userMessageContent}"`}
                />
                <Box sx={{mt: 1}}>{renderEvaluationStatus(item.evaluation, tr)}</Box>
              </ListItem>
            )
          )}

          {renderDetailSection(tr('A2 - 回應準確性', 'A2 - Response Accuracy'), evaluation.a2_feedback,
            (item, index) => (
              <ListItem key={index} divider sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                <ListItemText primary={`${tr('AI 回應', 'AI response')}: "${item.aiResponseContent}"`} />
                <Box sx={{mt: 1}}>
                    {renderBooleanStatus(item.isIncorrect, tr('標記為不準確', 'Marked inaccurate'), tr('未標記', 'Not marked'), "error", "success")}
                    {item.isIncorrect && item.comment && (
                         <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                             {tr('評論', 'Comment')}: {item.comment}
                         </Typography>
                    )}
                </Box>
              </ListItem>
            )
          )}

          {renderDetailSection(tr('A3 - 教學引導準確性', 'A3 - Coaching Guidance Accuracy'), evaluation.a3_feedback,
            (item, index) => (
              <ListItem key={index} divider sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                <ListItemText primary={`${tr('教學引導', 'Guidance')}: "${item.guidanceContent}"`} />
                 <Box sx={{mt: 1}}>
                    {renderBooleanStatus(item.isIncorrect, tr('標記為不準確', 'Marked inaccurate'), tr('未標記', 'Not marked'), "error", "success")}
                    {item.isIncorrect && item.comment && (
                         <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                             {tr('評論', 'Comment')}: {item.comment}
                         </Typography>
                    )}
                </Box>
              </ListItem>
            )
          )}

          {/* Manually construct B1 Section with Link */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  {tr('B1 - AI 評分準確性（節點查核點）', 'B1 - AI Scoring Accuracy (Node Checkpoints)')}
                </Typography>
                {evaluation.conversation_id && ( // Only show link if conversation_id exists
                  <RouterLink
                    to={`/history?view=${evaluation.conversation_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline' }}
                  >
                    <Typography variant="body2" color="primary">{tr('查看歷史訊息', 'View conversation history')}</Typography>
                  </RouterLink>
                )}
              </Box>
            </Box>
            {Array.isArray(evaluation.b1_correctness) && evaluation.b1_correctness.length > 0 ? (
              <List dense disablePadding>
                {evaluation.b1_correctness.map((item, index) => (
                  <ListItem key={index} divider sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                    <ListItemText
                      primary={`${tr('查核點', 'Checkpoint')} (${item.nodeText}): "${item.checkpointText}"`}
                    />
                    <Box sx={{mt: 1}}>{renderEvaluationStatus(item.evaluation, tr)}</Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">{tr('無此項目的評估資料。', 'No evaluation data for this item.')}</Typography>
            )}
          </Paper>

          {renderDetailSection(tr('B2 - 評分報告全面性（常見錯誤）', 'B2 - Report Coverage (Common Mistakes)'), evaluation.b2_missed,
            (item, index) => (
              <ListItem key={index} divider sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                 <ListItemText primary={`${tr('常見錯誤', 'Common mistake')}: "${item.commonMistakeText}"`} />
                  <Box sx={{mt: 1}}>
                      {renderBooleanStatus(item.missed, tr('標記為遺漏', 'Marked missed'), tr('未標記', 'Not marked'), "warning", "success")}
                  </Box>
              </ListItem>
            )
          )}

          {/* B3 Section - Combining Expectation/Comment and Rating */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 2 }}>
                {tr('B3 - 評分報告專業度', 'B3 - Report Professionalism')}
            </Typography>
            {/* Overall Suggestion Expectation/Comment */}
            {Array.isArray(evaluation.b3_details) && evaluation.b3_details.length > 0 ? (
                <List dense disablePadding sx={{ mb: 2 }}>
                {evaluation.b3_details.map((item, index) => {
                    // The item now contains suggestionText, evaluation, and comment directly
                    return (
                    <ListItem key={item.key || index} divider sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                         <ListItemText
                             primary={`${tr('整體建議評估', 'Overall suggestion evaluation')}:`}
                             secondary={`${tr('建議內容', 'Suggestion')}: "${item.suggestionText || 'N/A'}"`} // Display suggestion text
                         />
                         <Box sx={{mt: 1}}>
                            {renderEvaluationStatus(item.evaluation, tr)}
                            {item.comment && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                                    {tr('評論', 'Comment')}: {item.comment}
                                </Typography>
                            )}
                        </Box>
                    </ListItem>
                    );
                })}
                </List>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{tr('無整體建議評估資料。', 'No overall suggestion evaluation data.')}</Typography>
            )}

            {/* Overall Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} mr={2}>{tr('整體專業度評分:', 'Overall professionalism rating:')}</Typography>
                {evaluation.b3_rating !== null ? (
                    <Rating value={evaluation.b3_rating} readOnly />
                ) : (
                    <Typography variant="body2" color="text.secondary">{tr('未評分', 'Not rated')}</Typography>
                )}
            </Box>
          </Paper>

        </Box>
      )}
    </Container>
  );
}

export default EvaluationDetail;
